import React, { useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import db from '../../services/query';
import PrintIcon from '../../icons/print-icon';
import ExcelIcon from '../../icons/excel-icon';

export const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
export const reportMatrix = (rows, columns) => [columns.map(c => c.label), ...rows.map((row, i) => columns.map(c => (c.html || c.value) ? (c.html || c.value)(row, i) : row[c.path] ?? ''))];

const PRINT_BASE_STYLE = `
@page{size:A4 landscape;margin:8mm}
*{box-sizing:border-box}
html,body{margin:0;width:auto;height:auto;overflow:visible;zoom:1;transform:none}
body{padding:6mm 7mm 10mm;font-family:Tahoma,"Noto Sans Arabic",Arial,sans-serif;font-size:11pt;line-height:1.35;color:#111827;background:#fff}
.print-actions{text-align:center;padding:8px 0 14px}
.print-actions button{background:#075da9;color:#fff;border:0;border-radius:6px;padding:9px 24px;cursor:pointer;font:inherit;font-size:12pt}
.print-head{display:flex;align-items:center;justify-content:space-between;gap:8px;min-height:36mm;padding:2mm 1mm 3mm;margin-bottom:3mm}
.print-head-side{width:28%;font-size:10pt;line-height:1.45;color:#1f2937}
.print-head-side.is-left{text-align:left}
.print-head-center{width:44%;text-align:center}
.print-head-center img{width:65mm;max-width:90%;height:auto;object-fit:contain}
.print-head-center strong{display:block;margin-top:2mm;color:#075da9;font-size:12pt}
.print-title{text-align:center;margin:0 0 2mm;font-size:15pt;font-weight:700;color:#111827}
.print-subtitle{text-align:center;margin:0 0 4mm;color:#374151;font-size:10pt}
table{border-collapse:collapse;width:100%;table-layout:fixed}
th,td{border:1px solid #64748b;padding:2mm 1.6mm;text-align:right;vertical-align:middle;overflow-wrap:break-word;word-break:normal;line-height:1.35;color:#111827}
th{background:#eef3f8;color:#1f2937;font-weight:700}
thead{display:table-header-group}
tr{break-inside:avoid}
td img{display:block;margin:0 auto;object-fit:cover}
.print-footer{margin-top:8mm;display:flex;justify-content:space-around;gap:16px;padding-top:3mm}
.print-footer p{min-width:30mm;text-align:center;margin:0;color:#1f2937}
.print-footer span{display:block;border-top:1px solid #333;margin:0 8px 8px;height:10mm}
.print-sheet{break-after:page}.print-sheet:last-child{break-after:auto}
@media print{.print-actions{display:none}body{padding:0}.print-head-center img{width:60mm;max-width:90%;height:auto}}
`;

export function tableHtml(matrix, columns = null) {
  const head = matrix[0] || [];
  const htmlFlags = Array.isArray(columns) ? columns.map(c => Boolean(c?.html)) : head.map(() => false);
  return `<table><thead><tr>${head.map(v => `<th>${escapeHtml(v)}</th>`).join('')}</tr></thead><tbody>${matrix.slice(1).map(row => `<tr>${row.map((v, i) => `<td>${htmlFlags[i] ? String(v ?? '') : escapeHtml(v)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
}

export function buildPrintDocument({ title, subtitle = '', bodyHtml = '', style = '', footer = true }) {
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : '';
  const logo = `${origin}/login-assets/aria-university-logo.svg`;
  const footerHtml = footer
    ? `<div class="print-footer"><p><span></span>آمریت محترم دیپارتمنت</p><p><span></span>آمریت محترم پوهنحی</p><p><span></span>معاونت محترم علمی</p></div>`
    : '';
  return `<!DOCTYPE html><html lang="fa" dir="rtl"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${PRINT_BASE_STYLE}${style}</style></head><body>
<div class="print-actions"><button type="button" onclick="window.print()">چاپ / Print</button></div>
<header class="print-head">
  <div class="print-head-side">جمهوری اسلامی افغانستان<br>وزارت تحصیلات عالی<br>پوهنتون آریا</div>
  <div class="print-head-center"><img src="${logo}" alt="پوهنتون آریا"></div>
  <div class="print-head-side is-left">Islamic Republic of Afghanistan<br>Ministry of Higher Education<br>Aria University</div>
</header>
<h1 class="print-title">${escapeHtml(title)}</h1>
${subtitle ? `<p class="print-subtitle">${escapeHtml(subtitle)}</p>` : ''}
${bodyHtml}
${footerHtml}
</body></html>`;
}

/** Open a real HTML document (blob URL) instead of about:blank. */
export function openPrintHtml(html, title = '') {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const target = window.open(url, '_blank');
  if (!target) {
    URL.revokeObjectURL(url);
    return null;
  }
  try { target.opener = null; } catch (_) { /* ignore */ }
  if (title) {
    try { target.document.title = title; } catch (_) { /* ignore */ }
  }
  setTimeout(() => URL.revokeObjectURL(url), 120000);
  return target;
}

/**
 * Print a matrix report. Prefer this over window.open('') + document writes.
 * @returns {Window|null}
 */
export function printReport(title, matrix, subtitle = '', options = {}) {
  const columns = options.columns || null;
  const bodyHtml = options.bodyHtml || tableHtml(matrix, columns);
  const html = buildPrintDocument({
    title,
    subtitle,
    bodyHtml,
    style: options.style || '',
    footer: options.footer !== false,
  });
  return openPrintHtml(html);
}

export async function allReportPages(route) {
  const [path, search = ''] = route.split('?');
  const params = new URLSearchParams(search);
  params.set('limit', '100');
  const rows = [];
  for (let page = 1; ; page += 1) {
    params.set('page', String(page));
    const { data } = await db.getAll(`${path}?${params}`);
    if (!data?.success || !Array.isArray(data.data)) throw new Error('پاسخ گزارش معتبر نیست.');
    rows.push(...data.data);
    if (page >= Number(data.totalPages || 1)) break;
  }
  return rows;
}

export default function ParityReport({ title, columns, loadRows, subtitle = '', disabled = false, print = true, excel = true, icons = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async mode => {
    setBusy(true); setError('');
    try {
      const rows = await loadRows();
      const matrix = reportMatrix(rows, columns);
      if (mode === 'print') {
        const target = printReport(title, matrix, subtitle, { columns });
        if (!target) setError('اجازه باز کردن پنجره چاپ را فعال کنید.');
      } else {
        const book = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet(matrix.map(row => row.map(cell => {
          if (typeof cell === 'string' && cell.includes('<')) {
            const tmp = document.createElement('div');
            tmp.innerHTML = cell;
            return tmp.textContent || '';
          }
          return cell;
        })));
        sheet['!cols'] = columns.map(c => ({ wch: c.width || 22 }));
        sheet['!autofilter'] = { ref: sheet['!ref'] };
        book.Workbook = { Views: [{ RTL: true }] };
        XLSX.utils.book_append_sheet(book, sheet, 'گزارش');
        XLSX.writeFile(book, `${title}.xlsx`);
      }
    } catch (err) { setError(err?.response?.data?.error?.message || err.message || 'گزارش آماده نشد.'); }
    finally { setBusy(false); }
  };
  const btnClass = icons ? 'circle' : 'mis-button is-secondary';
  return <div className={`parity-report-actions${icons ? ' is-icons' : ''}`}>
    {print && <button type="button" className={`${btnClass}${icons ? ' print' : ''}`} title="چاپ" aria-label="چاپ" disabled={disabled || busy} onClick={() => run('print')}>{icons ? <PrintIcon /> : 'چاپ'}</button>}
    {excel && <button type="button" className={`${btnClass}${icons ? ' excel' : ''}`} title="Excel" aria-label="Excel" disabled={disabled || busy} onClick={() => run('excel')}>{icons ? <ExcelIcon /> : 'Excel'}</button>}
    {busy && <span role="status">در حال آماده‌سازی…</span>}
    {error && <span role="alert">{error}</span>}
  </div>;
}
